/**
 * LifecycleDrawer — single operator-visible diagnostic surface.
 *
 * Constitutional reference: PLATFORM_REBUILD_DOCTRINE.md §F.
 * Architecture references:
 *   CANONICAL_UPLOAD_ARCHITECTURE.md §7 (drawer requirements)
 *   PLATFORM_REBUILD_ARCHITECTURE.md §8 (8-item export bundle)
 *
 * Phase A scope:
 *   - Toggled by `?diag=1` in the URL.
 *   - Reads from OperationalStateContext (read-only).
 *   - Latest event pinned at the top.
 *   - Copy / Export buttons produce the 8-item operational story.
 *   - Healthcheck strip is a Phase E concern (endpoint doesn't exist
 *     yet). Drawer leaves a placeholder slot so Phase E can wire it
 *     without re-laying-out the drawer.
 *
 * Out of scope (deliberately): backend audit fetch, ?diag=1 cross-
 * route polling, "Send to operator" button.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useOperationalState } from "../../context/OperationalStateContext";

const BUILD_STAMP = "phaseA.may22.2026.r1-lifecycle-drawer";

function isDiagEnabled(search) {
  try {
    const sp = new URLSearchParams(search || "");
    return sp.get("diag") === "1";
  } catch (_) { return false; }
}

function readableLabel(event_name) {
  // No jargon. The drawer must be readable by a non-engineer operator.
  const map = {
    picker_open: "Picker opened",
    picker_files_arrived: "Files arrived from Photos",
    picker_handoff_accepted: "Files accepted into basket",
    picker_handoff_empty: "Photos returned with no files",
    basket_row_staged: "Clip staged",
    upload_init_requested: "Upload starting",
    upload_init_acked: "Upload session created",
    upload_chunk_uploaded: "Chunk uploaded",
    upload_chunk_retried: "Retrying after a hiccup",
    upload_complete_requested: "Finalising upload",
    upload_complete_acked: "Upload finalised",
    upload_resume_attempted: "Resuming an earlier upload",
    upload_session_expired: "Upload session expired",
    upload_integrity_failed: "Upload integrity check failed",
    basket_processed: "Sent for analysis",
    recovery_redirect_blocked: "Redirect blocked (upload protected)",
  };
  return map[event_name] || event_name;
}

function statusToneFor(event_name) {
  if (event_name === "upload_chunk_retried") return "retry";
  if (event_name === "picker_handoff_empty"
      || event_name === "upload_session_expired"
      || event_name === "upload_integrity_failed") return "failed";
  if (event_name === "basket_processed"
      || event_name === "upload_complete_acked") return "ok";
  return "info";
}

function deviceFingerprint() {
  if (typeof navigator === "undefined") return {};
  return {
    ua: (navigator.userAgent || "").slice(0, 200),
    platform: navigator.platform || null,
    touch_points: navigator.maxTouchPoints || 0,
    languages: (navigator.languages || []).slice(0, 4),
    online: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
    pixel_ratio: typeof window !== "undefined" ? (window.devicePixelRatio || null) : null,
    viewport: typeof window !== "undefined"
      ? { w: window.innerWidth, h: window.innerHeight }
      : null,
    connection_type: navigator?.connection?.effectiveType || null,
  };
}

function buildOperationalBundle(state, extra = {}) {
  // 8-item operational story per PLATFORM_REBUILD_ARCHITECTURE.md §8.
  return {
    captured_at: new Date().toISOString(),
    trace_id: state.trace_id,
    upload_ids: Object.keys(state.uploads || {}),
    basket_ids: Object.keys(state.baskets || {}),
    review_ids: Object.keys(state.reviews || {}),
    build_stamp: BUILD_STAMP,
    route: extra.route,
    device: deviceFingerprint(),
    visibility_transitions: extra.visibility_transitions || [],
    recovery_attempts: extra.recovery_attempts || [],
    events: state.events,
    healthcheck_polls: [],   // Phase E populates
  };
}

export default function LifecycleDrawer() {
  const location = useLocation();
  const { state } = useOperationalState();
  const [open, setOpen] = useState(false);
  const [visibilityLog, setVisibilityLog] = useState([]);
  const [copyStatus, setCopyStatus] = useState(null);

  const diagEnabled = isDiagEnabled(location.search);

  useEffect(() => {
    if (!diagEnabled) return undefined;
    const onVis = () => {
      setVisibilityLog((log) => [
        ...log.slice(-49),
        { at: new Date().toISOString(), state: document.visibilityState },
      ]);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [diagEnabled]);

  const eventsReversed = useMemo(() => [...state.events].reverse(), [state.events]);
  const latest = eventsReversed[0] || null;

  const onCopy = async () => {
    const bundle = buildOperationalBundle(state, {
      route: location.pathname + location.search,
      visibility_transitions: visibilityLog,
    });
    try {
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
      setCopyStatus("copied");
      setTimeout(() => setCopyStatus(null), 1500);
    } catch (_) {
      setCopyStatus("failed");
    }
  };

  const onExport = () => {
    const bundle = buildOperationalBundle(state, {
      route: location.pathname + location.search,
      visibility_transitions: visibilityLog,
    });
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `lifecycle-${state.trace_id || "session"}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!diagEnabled) return null;

  return (
    <aside
      data-testid="lifecycle-drawer"
      className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-amber-400/40 bg-slate-950/95 backdrop-blur text-slate-100"
      style={{ maxHeight: open ? "62vh" : "auto" }}
    >
      {/* Header — always visible when diag is on */}
      <header className="flex items-center justify-between px-3 py-1.5 text-[11px] font-mono">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="lifecycle-drawer-toggle"
          className="text-amber-300 font-extrabold tracking-wider"
        >
          {open ? "▾" : "▸"} Lifecycle ({state.events.length})
        </button>
        <span data-testid="lifecycle-drawer-context" className="text-slate-400 truncate">
          route {location.pathname} · build {BUILD_STAMP}
        </span>
        <span data-testid="lifecycle-drawer-upload-id" className="text-emerald-300 break-all max-w-[40%] truncate">
          {Object.keys(state.uploads).slice(-1)[0] || "(no upload yet)"}
        </span>
      </header>

      {/* Latest event pulse — always visible when diag is on */}
      {latest && (
        <div
          data-testid="lifecycle-drawer-latest"
          className="px-3 py-1.5 border-y border-amber-400/20 bg-amber-400/5 text-[11px] font-mono"
        >
          <span className="text-slate-500 mr-2">latest:</span>
          <span className="text-amber-200 font-bold">{readableLabel(latest.event_name)}</span>
          <span className="text-slate-500 ml-2">{latest.server_time.slice(11, 23)}</span>
        </div>
      )}

      {open && (
        <div className="flex flex-col gap-2 px-3 py-2 overflow-hidden">
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={onCopy}
              data-testid="lifecycle-drawer-copy"
              className="rounded bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold px-3 py-1.5 text-[11px]"
            >
              {copyStatus === "copied" ? "Copied ✓" : copyStatus === "failed" ? "Copy failed" : "Copy diagnostics"}
            </button>
            <button
              type="button"
              onClick={onExport}
              data-testid="lifecycle-drawer-export"
              className="rounded bg-slate-700 hover:bg-slate-600 text-slate-100 font-bold px-3 py-1.5 text-[11px]"
            >
              Export diagnostics
            </button>
            <span
              data-testid="lifecycle-drawer-healthcheck"
              data-status="pending"
              className="ml-auto rounded bg-slate-800 px-2 py-1 text-[10px] text-slate-400 font-mono"
            >
              healthcheck · pending (Phase E)
            </span>
          </div>

          <ul className="overflow-y-auto text-[10.5px] font-mono space-y-0.5" style={{ maxHeight: "48vh" }}>
            {eventsReversed.length === 0 && (
              <li className="text-slate-500 italic" data-testid="lifecycle-drawer-empty">no events yet — interact with the page to start logging</li>
            )}
            {eventsReversed.map((ev) => (
              <li
                key={ev.event_id}
                data-testid="lifecycle-drawer-event"
                data-event-name={ev.event_name}
                data-tone={statusToneFor(ev.event_name)}
                className="flex items-baseline gap-2 px-1.5 py-0.5 rounded hover:bg-slate-800/40"
              >
                <span className="text-slate-500 w-16 flex-none">{ev.server_time.slice(11, 23)}</span>
                <span
                  className={
                    statusToneFor(ev.event_name) === "ok" ? "text-emerald-300"
                    : statusToneFor(ev.event_name) === "retry" ? "text-amber-300"
                    : statusToneFor(ev.event_name) === "failed" ? "text-rose-300"
                    : "text-slate-300"
                  }
                >
                  {readableLabel(ev.event_name)}
                </span>
                {Object.keys(ev.payload || {}).length > 0 && (
                  <span className="text-slate-500 truncate">
                    {JSON.stringify(ev.payload).slice(0, 200)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}
